import apiClient from './api';
import axios from 'axios';
import config from '../config';

// Önbellek mekanizması
const cache = {
  neighborhoods: null
};

// Hata loglama yardımcı fonksiyonu
const logError = (error) => {
  console.error('Error in neighborhoodService:', error.message);
  if (error.response) {
    console.error('Status:', error.response.status);
    console.error('Data:', error.response.data);
  }
};

// Mahalle verilerini filtreleme fonksiyonu
const filterNeighborhoods = (data) => {
  // Önce mahalle seviyesindeki verileri al
  const mahalleler = data.filter(item => 
    item.types.includes('administrative_area_level_4')
  );

  // Tekrarları kaldır
  const uniqueMahalleler = mahalleler.reduce((acc, current) => {
    const mahalleAdi = current.address_components.find(comp => 
      comp.types.includes('administrative_area_level_4')
    )?.long_name;

    if (mahalleAdi && !acc.some(item => 
      item.address_components.find(comp => 
        comp.types.includes('administrative_area_level_4')
      )?.long_name === mahalleAdi
    )) {
      acc.push(current);
    }
    return acc;
  }, []);

  return uniqueMahalleler;
};

export const neighborhoodService = {
  async getNeighborhoods(latitude, longitude, radius = 10000, maxResults = 25) {
    // Önbellekte varsa, önbellekten getir
    const cacheKey = `${latitude}_${longitude}_${radius}_${maxResults}`;
    if (cache.neighborhoods && cache.neighborhoods[cacheKey]) {
      return cache.neighborhoods[cacheKey];
    }
    
    try {
      // Kullanıcı token'ını al
      const userData = JSON.parse(localStorage.getItem('user_data'));
      const token = userData?.token;
      
      // Birden fazla kategori için paralel istekler
      const queries = [
        // Ana mahalle ve bölgeler
        'neighborhoods sublocality administrative_area_level_4',
        // Alt bölgeler ve sokaklar
        'route street_address premise',
        // İlçe ve büyük alanlar
        'locality administrative_area_level_2 administrative_area_level_3',
        // Önemli noktalar ve yerler
        'establishment point_of_interest',
        // Posta kodu alanları
        'postal_code administrative_area_level_5'
      ];

      const promises = queries.map(query => 
        axios.post(`${config.API_BASE_URL_V1}auto-complete/dynamic-query`, 
          { 
            query: `${query} near ${latitude},${longitude}`,
            radius: radius,
            maxResults: Math.ceil(maxResults / queries.length)
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            },
            withCredentials: true
          }
        ).then(response => ({
          data: response.data.data?.results || [],
          queryType: query
        })).catch(error => {
          console.warn(`Failed to fetch neighborhoods for ${query}:`, error);
          return { data: [], queryType: query };
        })
      );

      const responses = await Promise.all(promises);
      
      // Tüm sonuçları birleştir
      let allNeighborhoods = [];
      
      responses.forEach(response => {
        if (response.data && Array.isArray(response.data)) {
          const filteredData = response.data.filter(item => {
            // Geçerli adres bileşenine sahip olmalı
            return item.formatted_address && 
                   item.geometry?.location?.lat && 
                   item.geometry?.location?.lng;
          });
          allNeighborhoods.push(...filteredData);
        }
      });

      // Mesafeye göre işle ve sırala
      const neighborhoodsWithDistance = allNeighborhoods.map(neighborhood => {
        const distance = calculateDistance(
          latitude, 
          longitude, 
          neighborhood.geometry?.location?.lat || 0, 
          neighborhood.geometry?.location?.lng || 0
        );
        return { ...neighborhood, distance };
      });

      // Gelişmiş filtreleme işlemi
      const filteredNeighborhoods = filterNeighborhoods(neighborhoodsWithDistance);
      
      // Sırala: önce tip önceliği, sonra mesafe
      const sortedNeighborhoods = filteredNeighborhoods.sort((a, b) => {
        // Önce mahalle tiplerini öne çıkar
        const aIsNeighborhood = a.types?.some(type => 
          ['neighborhood', 'sublocality', 'administrative_area_level_4'].includes(type)
        );
        const bIsNeighborhood = b.types?.some(type => 
          ['neighborhood', 'sublocality', 'administrative_area_level_4'].includes(type)
        );
        
        if (aIsNeighborhood && !bIsNeighborhood) return -1;
        if (!aIsNeighborhood && bIsNeighborhood) return 1;
        
        // Sonra mesafeye göre sırala
        return a.distance - b.distance;
      });
      
      // İlk N kadar sonucu al
      const finalResults = sortedNeighborhoods.slice(0, maxResults);
      
      // Sonucu önbelleğe kaydet
      if (!cache.neighborhoods) {
        cache.neighborhoods = {};
      }
      cache.neighborhoods[cacheKey] = finalResults;
      
      return finalResults;
    } catch (error) {
      logError(error);
      throw error;
    }
  },

  async getRegions({ type = 'neighborhoods', place = '', radius = 8000, maxResults = 20 }) {
    try {
      const userData = JSON.parse(localStorage.getItem('user_data'));
      const token = userData?.token;
  
      // Birden fazla sorgu tipi ile paralel arama
      const queries = [
        `${type} ${place}`,
        `neighborhoods near ${place}`,
        `districts sublocality ${place}`,
        `administrative areas ${place}`
      ];

      const promises = queries.map(query => 
        axios.post(
          `${config.API_BASE_URL_V1}auto-complete/dynamic-query`,
          { 
            query,
            radius,
            maxResults: Math.ceil(maxResults / queries.length)
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'Authorization': token ? `Bearer ${token}` : ''
            },
            withCredentials: true
          }
        ).then(response => ({
          results: response.data.data?.results || [],
          queryType: query
        })).catch(error => {
          console.warn(`Failed to fetch regions for ${query}:`, error);
          return { results: [], queryType: query };
        })
      );

      const responses = await Promise.all(promises);
      
      // Tüm sonuçları birleştir
      let allResults = [];
      responses.forEach(response => {
        if (response.results && Array.isArray(response.results)) {
          allResults.push(...response.results);
        }
      });

      // Duplicate'ları temizle ve filtrele
      const uniqueResults = allResults.reduce((acc, current) => {
        const isDuplicate = acc.some(item => 
          item.place_id === current.place_id || 
          item.formatted_address === current.formatted_address
        );
        
        if (!isDuplicate && current.formatted_address && current.geometry?.location) {
          acc.push(current);
        }
        return acc;
      }, []);

      return { results: uniqueResults };
  
    } catch (error) {
      logError(error);
      throw error;
    }
  }
  
};

// Mesafe hesaplama fonksiyonu
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const toRad = (value) => (value * Math.PI) / 180;
  const R = 6371; // Dünya yarıçapı (km)
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c; // Mesafe (km)
};

export default neighborhoodService; 